import { z } from 'zod';
import { ApiError, type Schemas } from '../../api/client';

export type Product = Schemas['Product'];
export type Variant = Schemas['Variant'];
export type VariantSummary = Schemas['VariantSummary'];
export type Unit = Schemas['Unit'];
export type Category = Schemas['Category'];
export type Brand = Schemas['Brand'];
export type Attribute = Schemas['Attribute'];
export type ProductUnit = Product['units'][number];
export type ProductImage = Product['images'][number];

export const catalogKeys = {
  all: ['catalog'] as const,
  products: ['catalog', 'products'] as const,
  productList: (params: object) => ['catalog', 'products', 'list', params] as const,
  product: (id: string) => ['catalog', 'products', 'detail', id] as const,
  units: ['catalog', 'units'] as const,
  categories: ['catalog', 'categories'] as const,
  posCategories: ['catalog', 'pos-categories'] as const,
  brands: ['catalog', 'brands'] as const,
  attributes: ['catalog', 'attributes'] as const,
  lookup: (code: string) => ['catalog', 'lookup', code] as const,
  image: (imageId: string) => ['catalog', 'image', imageId] as const,
};

/** Idempotency-Key header for POSTs whose schema requires it (one key per call). */
export function idempotency(): { 'idempotency-key': string } {
  return { 'idempotency-key': crypto.randomUUID() };
}

// --- patterns (mirror the API's) ---------------------------------------------------------

export const CODE_PATTERN = /^[0-9A-Za-z][0-9A-Za-z._\-/]{0,59}$/;
export const BARCODE_PATTERN = /^[0-9A-Za-z.-]{4,64}$/;
const AMOUNT = /^\d{1,15}(\.\d{1,4})?$/;
const FACTOR = /^\d{1,15}(\.\d{1,6})?$/;
const NUMBER = /^\d{1,15}(\.\d{1,6})?$/;

const amount = (label: string) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .refine((v) => AMOUNT.test(v), 'Enter an amount like 250 or 249.50');
const optionalAmount = z
  .string()
  .trim()
  .refine((v) => v === '' || AMOUNT.test(v), 'Enter a number like 200 or 12.5');

const code = (label: string) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(60, 'At most 60 characters')
    .refine(
      (v) => v === '' || CODE_PATTERN.test(v),
      'Letters, digits and . _ - / only; start with a letter or digit',
    );

// --- variants ----------------------------------------------------------------------------

export const variantFormSchema = z.object({
  /** Kept for edits; empty for new variants. */
  id: z.string(),
  nameSuffix: z.string().trim().max(120, 'At most 120 characters'),
  sku: code('SKU'),
  barcode: z
    .string()
    .trim()
    .refine((v) => v === '' || BARCODE_PATTERN.test(v), '4–64 letters or digits (no spaces)'),
  salePrice: amount('Sale price'),
  purchasePrice: optionalAmount,
  minStockLevel: optionalAmount,
  reorderQty: optionalAmount,
  isActive: z.boolean(),
  /** definitionId → optionId (OPTION), text (TEXT) or number (NUMBER); '' = not set. */
  attributes: z.record(z.string(), z.string().optional()),
});

export type VariantForm = z.infer<typeof variantFormSchema>;

export function emptyVariant(sku = ''): VariantForm {
  return {
    id: '',
    nameSuffix: '',
    sku,
    barcode: '',
    salePrice: '',
    purchasePrice: '',
    minStockLevel: '',
    reorderQty: '',
    isActive: true,
    attributes: {},
  };
}

/** "245.0000" → "245", "12.5000" → "12.5". */
export function trimDecimal(value: string | null | undefined): string {
  if (value === null || value === undefined) return '';
  if (!value.includes('.')) return value;
  return value.replace(/\.?0+$/, '');
}

export function variantToForm(v: Variant): VariantForm {
  const attributes: Record<string, string | undefined> = {};
  for (const a of v.attributes) {
    attributes[a.definitionId] =
      a.dataType === 'OPTION'
        ? (a.optionId ?? '')
        : a.dataType === 'NUMBER'
          ? trimDecimal(a.value)
          : a.value;
  }
  return {
    id: v.id,
    nameSuffix: v.nameSuffix ?? '',
    sku: v.sku,
    barcode: v.barcode ?? '',
    salePrice: trimDecimal(v.salePrice),
    purchasePrice: trimDecimal(v.purchasePrice),
    minStockLevel: trimDecimal(v.minStockLevel),
    reorderQty: trimDecimal(v.reorderQty),
    isActive: v.isActive,
    attributes,
  };
}

/** Attribute values as the API wants them; unknown or empty entries are dropped. */
export function toAttributeInputs(
  values: Record<string, string | undefined>,
  definitions: Pick<Attribute, 'id' | 'dataType'>[],
): Schemas['AttributeValueInput'][] {
  const out: Schemas['AttributeValueInput'][] = [];
  for (const def of definitions) {
    const raw = values[def.id]?.trim() ?? '';
    if (raw === '') continue;
    if (def.dataType === 'OPTION') out.push({ definitionId: def.id, optionId: raw });
    else if (def.dataType === 'NUMBER') out.push({ definitionId: def.id, number: raw });
    else out.push({ definitionId: def.id, text: raw });
  }
  return out;
}

/** Checks attribute values that zod can't know about (number format per definition). */
export function attributeErrors(
  values: Record<string, string | undefined>,
  definitions: Pick<Attribute, 'id' | 'dataType' | 'name'>[],
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const def of definitions) {
    const raw = values[def.id]?.trim() ?? '';
    if (raw === '') continue;
    if (def.dataType === 'NUMBER' && !NUMBER.test(raw))
      errors[def.id] = 'Enter a number like 8 or 9.5';
    if (def.dataType === 'TEXT' && raw.length > 200) errors[def.id] = 'At most 200 characters';
  }
  return errors;
}

const blank = (v: string) => (v.trim() === '' ? null : v.trim());

export function toCreateVariantRequest(
  f: VariantForm,
  definitions: Pick<Attribute, 'id' | 'dataType'>[],
  opts: { canViewCost: boolean },
): Schemas['CreateVariantRequest'] {
  return {
    sku: f.sku.trim(),
    barcode: blank(f.barcode),
    nameSuffix: blank(f.nameSuffix),
    salePrice: f.salePrice.trim(),
    ...(opts.canViewCost ? { purchasePrice: blank(f.purchasePrice) } : {}),
    minStockLevel: blank(f.minStockLevel),
    reorderQty: blank(f.reorderQty),
    attributes: toAttributeInputs(f.attributes, definitions),
  };
}

/**
 * PATCH body for a variant. Prices are only sent when the user may change them (and the cost only
 * when they can see it), so a clerk without price rights can still fix a barcode.
 */
export function toUpdateVariantRequest(
  f: VariantForm,
  original: Variant,
  definitions: Pick<Attribute, 'id' | 'dataType'>[],
  opts: { canPrice: boolean; canViewCost: boolean },
): Schemas['UpdateVariantRequest'] {
  const body: Schemas['UpdateVariantRequest'] = {
    version: original.version,
    sku: f.sku.trim(),
    barcode: blank(f.barcode),
    nameSuffix: blank(f.nameSuffix),
    minStockLevel: blank(f.minStockLevel),
    reorderQty: blank(f.reorderQty),
    isActive: f.isActive,
    attributes: toAttributeInputs(f.attributes, definitions),
  };
  if (opts.canPrice) {
    if (trimDecimal(original.salePrice) !== f.salePrice.trim()) body.salePrice = f.salePrice.trim();
    if (opts.canViewCost && trimDecimal(original.purchasePrice) !== f.purchasePrice.trim())
      body.purchasePrice = blank(f.purchasePrice);
  }
  return body;
}

// --- alternate units ---------------------------------------------------------------------

export const unitRowSchema = z.object({
  unitId: z.string().min(1, 'Pick a unit'),
  /** How many of `ofUnitId` it holds (of the base unit when empty). */
  factor: z
    .string()
    .trim()
    .refine((v) => FACTOR.test(v), 'Enter a number like 12 or 0.5')
    .refine((v) => !FACTOR.test(v) || Number(v) > 0, 'Must be more than zero'),
  /** '' = the base unit; else another pack of the product ("1 carton = 50 box"). */
  ofUnitId: z.string(),
  isDefaultPurchase: z.boolean(),
  isDefaultSale: z.boolean(),
});

export type UnitRow = z.infer<typeof unitRowSchema>;

/**
 * Base units in each pack, working out packs made of other packs (carton = 50 box, box = 5 pc →
 * carton = 250 pc). null where it can't be worked out (bad number, loop, missing unit).
 */
export function resolveFactors(rows: UnitRow[], baseId: string | undefined): (number | null)[] {
  const byUnit = new Map(rows.map((r, i) => [r.unitId, i]));
  const memo = new Map<number, number | null>();
  const resolve = (i: number, path: number[]): number | null => {
    if (memo.has(i)) return memo.get(i) ?? null;
    if (path.includes(i)) return null;
    const r = rows[i];
    const qty = r && FACTOR.test(r.factor.trim()) ? Number(r.factor) : NaN;
    let out: number | null = null;
    if (r && qty > 0) {
      if (!r.ofUnitId || r.ofUnitId === baseId) out = qty;
      else {
        const j = byUnit.get(r.ofUnitId);
        const inner = j === undefined ? null : resolve(j, [...path, i]);
        out = inner === null ? null : Math.round(qty * inner * 1e6) / 1e6;
      }
    }
    memo.set(i, out);
    return out;
  };
  return rows.map((_, i) => resolve(i, []));
}

/**
 * Rules the API applies to alternate units, checked up front: not the base unit, no repeats, one
 * default each for buying and selling, and whole factors when the base unit can't be split.
 * Returns errors keyed by row index and field.
 */
export function unitRowErrors(
  rows: UnitRow[],
  base: { id: string; allowsFraction: boolean } | undefined,
): { index: number; field: keyof UnitRow; message: string }[] {
  const errors: { index: number; field: keyof UnitRow; message: string }[] = [];
  const seen = new Set<string>();
  const factors = resolveFactors(rows, base?.id);
  let purchase = 0;
  let sale = 0;
  rows.forEach((r, index) => {
    if (base && r.unitId === base.id)
      errors.push({ index, field: 'unitId', message: 'Already the base unit' });
    else if (r.unitId && seen.has(r.unitId))
      errors.push({ index, field: 'unitId', message: 'Listed twice' });
    seen.add(r.unitId);
    const total = factors[index];
    if (r.ofUnitId && r.ofUnitId !== base?.id && FACTOR.test(r.factor.trim()) && total === null)
      errors.push({
        index,
        field: 'ofUnitId',
        message: rows.some((o) => o.unitId === r.ofUnitId)
          ? 'A pack can’t hold itself'
          : 'Add that pack size first',
      });
    if (base && !base.allowsFraction && total !== null && !Number.isInteger(total))
      errors.push({
        index,
        field: 'factor',
        message: 'Whole number: the base unit can’t be split',
      });
    if (r.isDefaultPurchase && ++purchase > 1)
      errors.push({ index, field: 'isDefaultPurchase', message: 'Only one default for buying' });
    if (r.isDefaultSale && ++sale > 1)
      errors.push({ index, field: 'isDefaultSale', message: 'Only one default for selling' });
  });
  return errors;
}

export function toUnitInputs(
  rows: UnitRow[],
  baseId: string,
): Schemas['ReplaceProductUnitsRequest']['units'] {
  return rows.map((r) => ({
    unitId: r.unitId,
    ...(r.ofUnitId && r.ofUnitId !== baseId
      ? { contains: { qty: r.factor.trim(), unitId: r.ofUnitId } }
      : { factor: r.factor.trim() }),
    isDefaultPurchase: r.isDefaultPurchase,
    isDefaultSale: r.isDefaultSale,
  }));
}

export function productUnitToRow(u: ProductUnit): UnitRow {
  return {
    unitId: u.unitId,
    factor: trimDecimal(u.contains ? u.contains.qty : u.factor),
    ofUnitId: u.contains?.unitId ?? '',
    isDefaultPurchase: u.isDefaultPurchase,
    isDefaultSale: u.isDefaultSale,
  };
}

/**
 * Stock in pack sizes, biggest first: 1 234 pc with ctn = 600 and box = 12 → "2 ctn 2 box 10 pc".
 * Null when the product has no packs bigger than the base unit or nothing is on hand.
 */
export function packBreakdown(
  onHand: string,
  base: { symbol: string },
  units: { symbol: string; factor: string }[],
): string | null {
  const SCALE = 10_000; // quantities have at most 4 decimals
  let left = Math.round(Number(onHand) * SCALE);
  const packs = units
    .map((u) => ({ symbol: u.symbol, size: Math.round(Number(u.factor) * SCALE) }))
    .filter((u) => u.size > SCALE && u.size % SCALE === 0)
    .sort((a, b) => b.size - a.size);
  if (!packs.length || !Number.isFinite(left) || left <= 0) return null;
  const parts: string[] = [];
  for (const u of packs) {
    const n = Math.floor(left / u.size);
    if (n > 0) parts.push(`${String(n)} ${u.symbol}`);
    left -= n * u.size;
  }
  if (!parts.length) return null;
  if (left > 0) parts.push(`${String(left / SCALE)} ${base.symbol}`);
  return parts.join(' ');
}

/** "1 box = 16 sq ft"; a pack of packs: "1 ctn = 50 box = 250 pc". */
export function conversionLabel(
  unit: { symbol: string; contains?: { qty: string; symbol: string } | null },
  factor: string,
  base: { symbol: string },
) {
  const via =
    unit.contains && unit.contains.symbol !== base.symbol
      ? `${trimDecimal(unit.contains.qty)} ${unit.contains.symbol} = `
      : '';
  return `1 ${unit.symbol} = ${via}${trimDecimal(factor)} ${base.symbol}`;
}

// --- product form ------------------------------------------------------------------------

const warranty = z
  .string()
  .trim()
  .refine((v) => v === '' || (/^\d{1,3}$/.test(v) && Number(v) <= 600), 'Whole months, 0 to 600');

export const productDetailsSchema = z.object({
  code: code('Product code'),
  name: z.string().trim().min(1, 'Name is required').max(200, 'At most 200 characters'),
  description: z.string().trim().max(4000, 'At most 4000 characters'),
  categoryId: z.string(),
  brandId: z.string(),
  manufacturerId: z.string(),
  trackSerial: z.boolean(),
  warrantyMonths: warranty,
  availableInPos: z.boolean(),
  posCategoryId: z.string(),
});

export type ProductDetailsForm = z.infer<typeof productDetailsSchema>;

export type ProductType = Product['productType'];

/** Odoo-style product types: only goods can track stock or have a recipe. */
export const PRODUCT_TYPES: { value: ProductType; label: string; hint: string }[] = [
  {
    value: 'GOODS',
    label: 'Goods',
    hint: 'Physical items. Track stock, or leave it off for items made from components when sold.',
  },
  { value: 'SERVICE', label: 'Service', hint: 'Labour, fees and services: no stock.' },
  {
    value: 'COMBO',
    label: 'Combo',
    hint: 'A bundle or deal of several products sold together at one price.',
  },
];

export const productFormSchema = productDetailsSchema
  .extend({
    baseUnitId: z.string().min(1, 'Pick the unit you count stock in'),
    productType: z.enum(['GOODS', 'SERVICE', 'COMBO']),
    trackInventory: z.boolean(),
    units: z.array(unitRowSchema).max(20, 'At most 20 alternate units'),
    variants: z.array(variantFormSchema).min(1, 'Add at least one variant').max(100),
  })
  .superRefine((v, ctx) => {
    // SKUs and barcodes must be unique; catch repeats inside the form before the API does.
    const skus = new Map<string, number>();
    const barcodes = new Map<string, number>();
    v.variants.forEach((variant, i) => {
      const sku = variant.sku.trim().toLowerCase();
      if (sku) {
        if (skus.has(sku))
          ctx.addIssue({
            code: 'custom',
            path: ['variants', i, 'sku'],
            message: 'Same SKU as another variant',
          });
        skus.set(sku, i);
      }
      const barcode = variant.barcode.trim();
      if (barcode) {
        if (barcodes.has(barcode))
          ctx.addIssue({
            code: 'custom',
            path: ['variants', i, 'barcode'],
            message: 'Same barcode as another variant',
          });
        barcodes.set(barcode, i);
      }
    });
  });

export type ProductForm = z.infer<typeof productFormSchema>;

export function emptyProductForm(baseUnitId = ''): ProductForm {
  return {
    code: '',
    name: '',
    description: '',
    categoryId: '',
    brandId: '',
    manufacturerId: '',
    trackSerial: false,
    warrantyMonths: '',
    availableInPos: true,
    posCategoryId: '',
    baseUnitId,
    productType: 'GOODS',
    trackInventory: true,
    units: [],
    variants: [emptyVariant()],
  };
}

export function productToDetailsForm(p: Product): ProductDetailsForm {
  return {
    code: p.code,
    name: p.name,
    description: p.description ?? '',
    categoryId: p.category?.id ?? '',
    brandId: p.brand?.id ?? '',
    manufacturerId: p.manufacturer?.id ?? '',
    trackSerial: p.trackSerial,
    warrantyMonths: p.warrantyMonths === null ? '' : String(p.warrantyMonths),
    availableInPos: p.availableInPos,
    posCategoryId: p.posCategory?.id ?? '',
  };
}

export function toUpdateProductRequest(
  f: ProductDetailsForm,
  version: number,
): Schemas['UpdateProductRequest'] {
  return {
    version,
    code: f.code.trim(),
    name: f.name.trim(),
    description: blank(f.description),
    categoryId: f.categoryId || null,
    brandId: f.brandId || null,
    manufacturerId: f.manufacturerId || null,
    trackSerial: f.trackSerial,
    warrantyMonths: f.warrantyMonths.trim() === '' ? null : Number(f.warrantyMonths),
    availableInPos: f.availableInPos,
    posCategoryId: f.posCategoryId || null,
  };
}

export function toCreateProductRequest(
  f: ProductForm,
  definitions: Pick<Attribute, 'id' | 'dataType'>[],
  opts: { canViewCost: boolean },
): Schemas['CreateProductRequest'] {
  return {
    code: f.code.trim(),
    name: f.name.trim(),
    description: blank(f.description),
    categoryId: f.categoryId || null,
    brandId: f.brandId || null,
    manufacturerId: f.manufacturerId || null,
    baseUnitId: f.baseUnitId,
    productType: f.productType,
    trackInventory: f.productType === 'GOODS' && f.trackInventory,
    trackSerial: f.productType === 'GOODS' && f.trackInventory && f.trackSerial,
    warrantyMonths: f.warrantyMonths.trim() === '' ? null : Number(f.warrantyMonths),
    availableInPos: f.availableInPos,
    posCategoryId: f.posCategoryId || null,
    variants: f.variants.map((v) => toCreateVariantRequest(v, definitions, opts)),
    units: toUnitInputs(f.units, f.baseUnitId),
  };
}

/**
 * A SKU suggestion from the product code: the code itself for the first variant, then the code
 * plus the variant's name ("MT-PT" + "24x24 Glossy" → "MT-PT-24X24-GLOSSY") or its position.
 */
export function suggestSku(productCode: string, nameSuffix: string, index: number): string {
  const base = productCode
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-Z._\-/]/g, '');
  if (!base) return '';
  const suffix = nameSuffix
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-Z]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (suffix) return `${base}-${suffix}`.slice(0, 60);
  return index === 0 ? base : `${base}-${String(index + 1)}`;
}

// --- display helpers ---------------------------------------------------------------------

/** Lowest and highest sale price among active variants (all variants if none is active). */
export function priceRange(variants: Pick<Variant, 'salePrice' | 'isActive'>[]): {
  min: number;
  max: number;
} | null {
  const pool = variants.some((v) => v.isActive) ? variants.filter((v) => v.isActive) : variants;
  const prices = pool.map((v) => Number(v.salePrice)).filter(Number.isFinite);
  if (prices.length === 0) return null;
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

/** Gross margin on the sale price, in percent; null when the cost is unknown or price is 0. */
export function marginPercent(purchase: string | null, sale: string): number | null {
  if (purchase === null || purchase === '') return null;
  const p = Number(purchase);
  const s = Number(sale);
  if (!Number.isFinite(p) || !Number.isFinite(s) || s <= 0) return null;
  return ((s - p) / s) * 100;
}

export interface CategoryNode extends Category {
  depth: number;
}

/** Categories in tree order (parents before children, siblings by name) with their depth. */
export function categoryTree(categories: Category[]): CategoryNode[] {
  const byParent = new Map<string | null, Category[]>();
  for (const c of categories) {
    const key = c.parentId && categories.some((p) => p.id === c.parentId) ? c.parentId : null;
    byParent.set(key, [...(byParent.get(key) ?? []), c]);
  }
  const out: CategoryNode[] = [];
  const walk = (parent: string | null, depth: number) => {
    const children = [...(byParent.get(parent) ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    for (const c of children) {
      out.push({ ...c, depth });
      walk(c.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/** The category and everything under it: never valid as its own new parent. */
export function categoryWithDescendants(categories: Category[], id: string): Set<string> {
  const out = new Set([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const c of categories) {
      if (c.parentId && out.has(c.parentId) && !out.has(c.id)) {
        out.add(c.id);
        grew = true;
      }
    }
  }
  return out;
}

/** Image types the API accepts, with the size limit (MAX_IMAGE_BYTES default). */
export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const MAX_IMAGES = 10;

export function imageProblem(file: Pick<File, 'type' | 'size'>): string | null {
  if (!IMAGE_TYPES.includes(file.type)) return 'Use a JPEG, PNG or WebP image';
  if (file.size > MAX_IMAGE_BYTES) return 'Images can be at most 5 MB';
  return null;
}

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// --- API errors --------------------------------------------------------------------------

/**
 * Where an API error belongs in a product/variant form: validation `details` ("body.variants.0.sku")
 * and the uniqueness codes (PRODUCT_CODE_TAKEN, SKU_TAKEN, BARCODE_TAKEN, which name the value in
 * the message). Errors that fit no field return an empty list (show a toast instead).
 */
export function catalogFieldErrors(
  error: unknown,
  variants: Pick<VariantForm, 'sku' | 'barcode'>[] = [],
): { path: string; message: string }[] {
  if (!(error instanceof ApiError)) return [];
  if (error.details.length > 0)
    return error.details.map((d) => ({ path: d.path.replace(/^body\./, ''), message: d.message }));
  const find = (key: 'sku' | 'barcode') => {
    const i = variants.findIndex((v) => {
      const value = v[key].trim();
      return value !== '' && error.message.toLowerCase().includes(value.toLowerCase());
    });
    return i >= 0 ? i : variants.length === 1 ? 0 : -1;
  };
  switch (error.code) {
    case 'PRODUCT_CODE_TAKEN':
      return [{ path: 'code', message: 'Another product already uses this code' }];
    case 'SKU_TAKEN': {
      const i = find('sku');
      return i < 0
        ? []
        : [{ path: `variants.${String(i)}.sku`, message: 'Already used by another item' }];
    }
    case 'BARCODE_TAKEN': {
      const i = find('barcode');
      return i < 0
        ? []
        : [{ path: `variants.${String(i)}.barcode`, message: 'Already used by another item' }];
    }
    default:
      return [];
  }
}

/** Select slots that show the "none" option's text instead of an empty box under a floating label. */
export const EMPTY_SELECT_SLOTS = {
  inputLabel: { shrink: true },
  select: { displayEmpty: true },
} as const;

/**
 * A new-product form copying `p` (⋯ Duplicate): everything but the code, SKUs and barcodes, which
 * must be unique, and the stock, which a new product never has.
 */
export function productToCopyForm(p: Product): ProductForm {
  return {
    ...emptyProductForm(p.baseUnit.id),
    ...productToDetailsForm(p),
    code: '',
    name: `${p.name} (copy)`,
    productType: p.productType,
    trackInventory: p.trackInventory,
    units: p.units.filter((u) => u.unitId !== p.baseUnit.id).map(productUnitToRow),
    variants: p.variants.map((v) => ({ ...variantToForm(v), id: '', sku: '', barcode: '' })),
  };
}
